/**
 * The typed client, built from the contract in @hrms/shared.
 *
 * Nothing here describes a route. Every path, method, and body shape comes from the contract, so a
 * route that changes shape breaks compilation in the app that calls it rather than at runtime in
 * front of a user. That is the whole point of invariant 8.
 */

import {
  initClient,
  tsRestFetchApi,
  type ApiFetcher,
  type ApiFetcherArgs,
  type ClientArgs,
  type InitClientReturn,
} from '@ts-rest/core';
import {
  contract,
  isSalaryPinRequiredBody,
  tokenPairSchema,
  type SalaryPinRequiredDetails,
  type TokenPair,
} from '@hrms/shared';
import type { TokenStore } from './token-store.js';

export interface ApiClientOptions {
  /** No trailing slash — e.g. `https://api.hrmsai.com`. */
  baseUrl: string;
  tokens: TokenStore;
  /**
   * Called once when the session is definitively over: no refresh token, or the server refused the
   * one we had. The app routes to the login screen. Never called for an ordinary expired access
   * token, which the refresh below handles silently.
   */
  onSessionExpired?: () => void;
  /** Swapped in tests, and by React Native when a custom transport is needed. */
  fetchApi?: ApiFetcher;
  baseHeaders?: Record<string, string>;
  /**
   * Serialises the refresh ACROSS instances that share one persisted refresh token — i.e. across
   * browser tabs. The single-flight guard below is per token store, and a store lives in one tab,
   * so two tabs whose access tokens age out together each rotate the same refresh token; the
   * server sees the second rotation as a replay of a consumed token and revokes the whole family,
   * logging both tabs out. Wrapping the refresh in a cross-tab lock (web passes
   * `navigator.locks.request`) makes the second tab wait, then read the token the first one just
   * wrote — which is why `performRefresh` re-reads the store inside the lock rather than closing
   * over a value. Absent, refreshes run unserialised; a single-tab client (mobile) needs nothing
   * here.
   */
  refreshLock?: <T>(fn: () => Promise<T>) => Promise<T>;
  /**
   * The API refused a response that carries other people's pay until the salary PIN is entered (403
   * SALARY_PIN_REQUIRED, ADR 0041). Resolve true once unlocked and the request is sent once more; false gives the
   * caller the 403. Never called for the PIN routes themselves.
   */
  onSalaryPinRequired?: (details: SalaryPinRequiredDetails) => Promise<boolean>;
}

/** A 401 from these means "your credentials are wrong", not "your access token aged out". */
function isCredentialRoute(route: ApiFetcherArgs['route']): boolean {
  return route === contract.auth.login || route === contract.auth.refresh;
}

/** A route that takes no session at all — `routeMeta('public')` (ADR 0009). */
function isPublicRoute(route: ApiFetcherArgs['route']): boolean {
  return (route.metadata as { permission?: unknown } | undefined)?.permission === 'public';
}

/**
 * The salary PIN routes answer their own 403s (a locked PIN, a PIN not set yet): the dialog that sent them is
 * already the thing the hook would open, so asking again from inside it would wait on itself.
 */
const SALARY_PIN_ROUTES: ReadonlySet<unknown> = new Set(Object.values(contract.salaryPin));

function isSalaryPinRoute(route: ApiFetcherArgs['route']): boolean {
  return SALARY_PIN_ROUTES.has(route);
}

/** `authedFetch` has a URL, not a route: the PIN routes all live under `/salary-pin/`. */
function isSalaryPinPath(url: string): boolean {
  return /\/salary-pin\//.test(url.split('?')[0] ?? '');
}

// ---------------------------------------------------------------------------
// Refresh on 401 — one policy, shared by the ts-rest fetcher and `authedFetch`
// ---------------------------------------------------------------------------

/**
 * SINGLE-FLIGHT REFRESH, and it is not an optimisation.
 *
 * A dashboard mounts and fires ten queries at once. If the access token has just aged out, all
 * ten return 401 within a few milliseconds. Refreshing per response means ten concurrent
 * rotations of the SAME refresh token — and rotation revokes the token it consumed, so nine of
 * those requests present a token the server revoked microseconds earlier. The server cannot tell
 * that apart from a stolen token being replayed, so it does the only safe thing and kills the
 * entire session family (see decideRotation in @hrms/auth). The user is logged out by their own
 * client, on a normal page load, and the bug reproduces only under concurrency.
 *
 * One promise, shared by every waiter, is what keeps that from happening.
 *
 * Keyed on the token STORE rather than held per client instance, because the thing being
 * protected is the refresh token, and that lives in the store. An app holds one store and builds
 * everything on it — the ts-rest client, the TanStack bindings, and the raw `authedFetch` calls
 * (the copilot's SSE stream, multipart uploads) that cannot go through ts-rest. A copilot stream
 * and a dashboard query aging out together is the ten-queries scenario across two code paths, and
 * a guard per code path would not cover it. A WeakMap, so a discarded store takes its slot with it.
 */
const refreshesInFlight = new WeakMap<TokenStore, Promise<boolean>>();

/**
 * How a refresh reaches the server. The typed client sends it through its ts-rest transport, a raw
 * call through its `fetch`, so that whichever transport a test scripted is the one the refresh
 * uses. The refresh token is the only thing either ever sends to `/auth/refresh`.
 */
type RefreshTransport = (refreshToken: string) => Promise<{ status: number; body: unknown }>;

async function performRefresh(tokens: TokenStore, send: RefreshTransport): Promise<boolean> {
  const refreshToken = await tokens.getRefreshToken();
  if (!refreshToken) return false;

  const response = await send(refreshToken);

  // Someone put a different session in the store while this refresh was in flight — a sign-in, or
  // the session handoff a company switch lands on, which redeems a code on a subdomain that may still
  // hold an OLDER session's refresh token, possibly another person's from an earlier sign-in in this
  // browser. Whatever this refresh returned belongs to that old session: saving it would replace the
  // new session with the old person (seen in production on 2026-10-14 — Tanvi switched into Arham
  // Money and landed as Preeti, whose session this browser had kept since 8 October), and treating its
  // failure as "session over" would sign the new one out. Keep what is there; the caller replays with
  // it. Checked before the status, so both outcomes are covered.
  if ((await tokens.getRefreshToken()) !== refreshToken) return true;

  if (response.status !== 200) return false;

  // Parsed rather than trusted: this value becomes the credential on every subsequent request,
  // so a malformed body should fail here, loudly, and not as a puzzling 401 three calls later.
  const parsed = tokenPairSchema.safeParse((response.body as { tokens?: unknown })?.tokens);
  if (!parsed.success) return false;

  await tokens.save(parsed.data satisfies TokenPair);
  return true;
}

type RefreshOptions = Pick<
  ApiClientOptions,
  'tokens' | 'onSessionExpired' | 'refreshLock' | 'onSalaryPinRequired'
>;

/**
 * Refresh once, however many callers ask at the same time. Resolves true when new tokens are in
 * the store and the caller should replay its request; false when the session is over — the store
 * is already cleared and `onSessionExpired` has fired, so the caller only hands back the 401 it
 * was holding.
 */
async function refreshOrExpire(options: RefreshOptions, send: RefreshTransport): Promise<boolean> {
  const { tokens } = options;
  let inFlight = refreshesInFlight.get(tokens);
  if (!inFlight) {
    const withLock = options.refreshLock ?? ((fn) => fn());
    inFlight = withLock(() => performRefresh(tokens, send)).finally(() => {
      refreshesInFlight.delete(tokens);
    });
    refreshesInFlight.set(tokens, inFlight);
  }

  if (await inFlight) return true;
  await tokens.clear();
  options.onSessionExpired?.();
  return false;
}

/** The bearer header for the current access token — or nothing at all, never `Bearer null`. */
async function bearer(tokens: TokenStore): Promise<Record<string, string>> {
  const accessToken = await tokens.getAccessToken();
  return accessToken ? { authorization: `Bearer ${accessToken}` } : {};
}

function authenticatedFetcher(options: ApiClientOptions): ApiFetcher {
  const transport = options.fetchApi ?? tsRestFetchApi;

  // Deliberately calls `transport`, not the wrapper: routing the refresh through the
  // interceptor would let a 401 on refresh trigger another refresh, forever.
  const sendRefresh: RefreshTransport = (refreshToken) =>
    transport({
      route: contract.auth.refresh,
      path: `${options.baseUrl}${contract.auth.refresh.path}`,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      rawBody: { refreshToken },
      rawQuery: undefined,
      contentType: 'application/json',
    });

  return async (args) => {
    const send = async () =>
      transport({ ...args, headers: { ...args.headers, ...(await bearer(options.tokens)) } });

    // A session to resume but no access token yet — every full page load on the web starts here,
    // since the access token lives only in memory, and so does every company switch. Sending the
    // request anyway can only come back 401 and then refresh: one wasted round trip in front of
    // everything the page needs. Refresh first instead. Same single-flight and cross-tab lock as
    // the 401 path below, and the same outcome if the refresh is refused (store cleared, then the
    // request goes out without a bearer and gets the 401 the caller would have had anyway).
    // Public routes skip this: they need no session, so they need not wait for one.
    if (
      !isCredentialRoute(args.route) &&
      !isPublicRoute(args.route) &&
      !(await options.tokens.getAccessToken()) &&
      (await options.tokens.getRefreshToken())
    ) {
      await refreshOrExpire(options, sendRefresh);
    }

    const sendRefreshing = async () => {
      const first = await send();
      if (first.status !== 401 || isCredentialRoute(args.route)) return first;
      if (!(await refreshOrExpire(options, sendRefresh))) return first;
      return send();
    };

    const response = await sendRefreshing();

    // Pay behind the salary PIN (ADR 0041): ask once, then send once more. Once only — a second refusal
    // (the unlock lapsed in between, or the person cancelled) goes back to the caller as the 403 it is.
    // The resend refreshes on a 401 too: the access token can expire while the person looks for their PIN.
    const hook = options.onSalaryPinRequired;
    if (
      hook &&
      response.status === 403 &&
      !isSalaryPinRoute(args.route) &&
      isSalaryPinRequiredBody(response.body) &&
      (await hook(response.body.details))
    ) {
      return sendRefreshing();
    }
    return response;
  };
}

// ---------------------------------------------------------------------------
// Raw fetch with the same auth behaviour
// ---------------------------------------------------------------------------

/** The same options `createApiClient` takes, minus the ts-rest specific ones, so an app can reuse its. */
export interface AuthedFetchOptions extends RefreshOptions {
  baseUrl: string;
  /** Injected in tests; Expo passes `expo/fetch` when it needs a streaming response body. */
  fetch?: typeof fetch;
}

/** `RequestInit` with headers as a plain record, so the bearer merges in with a spread. */
export type AuthedFetchInit = Omit<RequestInit, 'headers'> & { headers?: Record<string, string> };

/**
 * `fetch` with the typed client's auth behaviour — bearer injected, one shared refresh on 401,
 * then the request replayed — for the calls that cannot go through ts-rest: the copilot's
 * Server-Sent Events stream (the ts-rest fetcher reads the whole body before returning) and
 * multipart uploads. Returns the raw `Response`; the caller owns the body.
 *
 * Only for bearer-authenticated routes: a 401 from `/auth/login` or `/auth/refresh` is a wrong
 * credential, not an aged token, and the typed client already knows not to retry those. The body
 * is sent twice on a replay, so it must be re-sendable — a string, `FormData`, a `Blob` — not a
 * one-shot stream.
 */
export async function authedFetch(
  options: AuthedFetchOptions,
  url: string,
  init: AuthedFetchInit = {},
): Promise<Response> {
  const fetchImpl = options.fetch ?? globalThis.fetch;

  const sendRefresh: RefreshTransport = async (refreshToken) => {
    const response = await fetchImpl(`${options.baseUrl}${contract.auth.refresh.path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    return { status: response.status, body: await response.json().catch(() => undefined) };
  };

  const send = async () =>
    fetchImpl(url, { ...init, headers: { ...init.headers, ...(await bearer(options.tokens)) } });

  const sendRefreshing = async () => {
    const first = await send();
    if (first.status !== 401) return first;
    if (!(await refreshOrExpire(options, sendRefresh))) return first;
    return send();
  };

  const response = await sendRefreshing();

  // Same salary PIN retry as the typed client, refreshing on a 401 the same way. The body is read from a clone, so the caller still owns
  // the original response whichever way this goes.
  const hook = options.onSalaryPinRequired;
  if (hook && response.status === 403 && !isSalaryPinPath(url)) {
    const body: unknown = await response
      .clone()
      .json()
      .catch(() => null);
    if (isSalaryPinRequiredBody(body) && (await hook(body.details))) return sendRefreshing();
  }
  return response;
}

/**
 * The arguments both the plain client and the TanStack Query bindings are built from, so the two
 * cannot drift into having different auth behaviour.
 */
export function createClientArgs(options: ApiClientOptions) {
  return {
    baseUrl: options.baseUrl,
    baseHeaders: options.baseHeaders ?? {},
    api: authenticatedFetcher(options),
    // Cookies are not the credential here; sending them cross-origin only widens CSRF surface.
    credentials: 'omit',
  } satisfies ClientArgs;
}

/**
 * Named, and used as the explicit return type below, on purpose: the contract is now large enough
 * that TypeScript refuses to serialise the inferred return type of `createApiClient` into its
 * declaration file (TS7056). Naming it makes the .d.ts refer to this alias instead of inlining the
 * whole router. The type is identical to what `initClient` returns for these arguments.
 */
export type ApiClient = InitClientReturn<typeof contract, ReturnType<typeof createClientArgs>>;

export function createApiClient(options: ApiClientOptions): ApiClient {
  return initClient(contract, createClientArgs(options));
}
